import { Rows3, X } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

/** The viewer's "condensed" choice for TV mode lives in this browser: a TV is its own device. */
const CONDENSED_KEY = "report-tv-condensed";

/**
 * Fullscreens the document while `on`. Escape (or any other exit from
 * fullscreen) fires fullscreenchange, which turns TV mode off again.
 */
export const useTvFullscreen = (on: boolean, setOn: (on: boolean) => void): void => {
	useEffect(() => {
		if (!on) return;
		document.documentElement.requestFullscreen?.().catch(() => undefined);
		const onChange = () => {
			if (!document.fullscreenElement) setOn(false);
		};
		document.addEventListener("fullscreenchange", onChange);
		return () => {
			document.removeEventListener("fullscreenchange", onChange);
			if (document.fullscreenElement) document.exitFullscreen?.().catch(() => undefined);
		};
	}, [on, setOn]);
};

/** Whether TV mode shows number tiles condensed (title beside the number), remembered per browser. */
export const useTvCondensed = (): [boolean, (condensed: boolean) => void] => {
	const [condensed, setState] = useState(() => {
		try {
			return localStorage.getItem(CONDENSED_KEY) === "1";
		} catch {
			return false;
		}
	});
	const setCondensed = useCallback((value: boolean) => {
		setState(value);
		try {
			localStorage.setItem(CONDENSED_KEY, value ? "1" : "0");
		} catch {
			// Private mode or blocked storage: the choice lasts for this page only.
		}
	}, []);
	return [condensed, setCondensed];
};

/** How long the pointer has to rest before the overlay controls fade out. */
const IDLE_MS = 3000;

/** True while the viewer is moving the pointer or pressing keys; false after IDLE_MS of nothing. */
const usePointerActive = (): boolean => {
	const [active, setActive] = useState(false);
	useEffect(() => {
		let timer: number | undefined;
		const wake = () => {
			setActive(true);
			window.clearTimeout(timer);
			timer = window.setTimeout(() => setActive(false), IDLE_MS);
		};
		const events = ["pointermove", "pointerdown", "keydown"] as const;
		events.forEach((e) => window.addEventListener(e, wake));
		return () => {
			events.forEach((e) => window.removeEventListener(e, wake));
			window.clearTimeout(timer);
		};
	}, []);
	return active;
};

interface TvControlsProps {
	condensed: boolean;
	onCondensed: (condensed: boolean) => void;
	onExit: () => void;
	/** Fade out while the pointer rests, so a TV shows nothing but the report. */
	fade?: boolean;
}

/** The buttons of TV mode: the condensed toggle and the way out. */
export const TvControls: React.FC<TvControlsProps> = ({
	condensed,
	onCondensed,
	onExit,
	fade = false,
}) => {
	const active = usePointerActive();
	return (
		<div
			className={cn(
				"flex items-center gap-2",
				fade && "transition-opacity duration-300",
				fade && !active && "pointer-events-none opacity-0"
			)}
		>
			<button
				type="button"
				onClick={() => onCondensed(!condensed)}
				aria-pressed={condensed}
				aria-label="Kompakt läge"
				title={
					condensed
						? "Kompakt läge på: nyckeltalen visar rubriken bredvid siffran så graferna får mer höjd. Klicka för att stänga av."
						: "Kompakt läge: nyckeltalen visar rubriken bredvid siffran så graferna får mer höjd."
				}
				className={cn(
					"rounded-md border p-1.5",
					condensed
						? "border-gray-900 bg-gray-900 text-white hover:bg-gray-800"
						: "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
				)}
			>
				<Rows3 className="h-4 w-4" />
			</button>
			<button
				type="button"
				onClick={onExit}
				className="rounded-md border border-gray-300 bg-white p-1.5 text-gray-600 hover:bg-gray-50"
				aria-label="Lämna TV-läge"
				title="Lämna TV-läge (Esc)"
			>
				<X className="h-4 w-4" />
			</button>
		</div>
	);
};

interface TvHeaderProps {
	name: string;
	/** Range, freshness and the like, shown next to the name. */
	meta: React.ReactNode;
	condensed: boolean;
	onCondensed: (condensed: boolean) => void;
	onExit: () => void;
}

/**
 * The one-line header of TV mode: name left, meta and controls right. Condensed,
 * it shrinks to a slim line and the controls fade out while the pointer rests.
 */
export const TvHeader: React.FC<TvHeaderProps> = ({
	name,
	meta,
	condensed,
	onCondensed,
	onExit,
}) => (
	<div className="flex items-center justify-between gap-3">
		<h1
			className={cn(
				"min-w-0 truncate font-semibold text-gray-900",
				condensed ? "text-base" : "text-2xl"
			)}
		>
			{name}
		</h1>
		<div
			className={cn(
				"flex shrink-0 items-center gap-3 text-gray-500",
				condensed ? "text-xs" : "text-sm"
			)}
		>
			<span>{meta}</span>
			<TvControls
				condensed={condensed}
				onCondensed={onCondensed}
				onExit={onExit}
				fade={condensed}
			/>
		</div>
	</div>
);
